import debounce from "debounce-promise";
import DOMPurify from "dompurify";
import parse from "html-react-parser";
import { ComponentProps, ReactNode, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useHistory } from "react-router";
import { components, GroupBase, InputProps, OptionProps } from "react-select";
import Select from "react-select/async";

import CircleButton from "@components/buttons/CircleButton";
import styles from "@components/fieldsNative/SearchInput.module.css";
import SearchInputWrapper from "@components/fieldsNative/SearchInputWrapper";
import TinyText from "@components/text/TinyText";
import FormError from "@components/typography/FormError";
import Heading3 from "@components/typography/Heading3";
import { searchSelectStyles } from "@utils/formUtils";

export type SearchOption = {
  category: string;
  highlight?: string | null;
  label: string;
  link?: string;
  onClick?: () => void;
  value: string;
};

export type SearchOptionGroup = {
  label: string;
  options: SearchOption[];
};

type Props = Pick<ComponentProps<typeof SearchInputWrapper>, "className"> & {
  color?: "white" | "neutral";
  error: string | null | undefined;
  errorValues?: { [key: string]: string | ReactNode };
  initialValue: string | undefined | null;
  instructions?: string | null;
  name: string;
  onSearch?: (input: string) => Promise<SearchOptionGroup[]>;
  placeholder?: string;
  required?: boolean;
  setValue: (value: string) => void;
};

const Input = ({ ...props }) => (
  <components.Input {...(props as InputProps<unknown, boolean, GroupBase<unknown>>)} data-lpignore="true" />
);

const Option = ({ ...props }) => {
  // eslint-disable-next-line react/prop-types
  const searchOption = props.data as SearchOption;
  const optionProps = props as OptionProps;
  const highlight = searchOption.highlight;

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const purifiedHighlight = highlight && parse(DOMPurify.sanitize(highlight, { ALLOWED_TAGS: ["em"] }));

  return (
    <components.Option {...optionProps} data-lpignore="true">
      {optionProps.children}
      <div className={styles.highlight}>{purifiedHighlight}</div>
    </components.Option>
  );
};

const ClearIndicator = ({ ...props }) => (
  <div>
    <CircleButton
      ariaLabelKey="accessibility.ariaLabels.delete"
      color="gray"
      fill="clear"
      icon="xmark"
      // eslint-disable-next-line react/prop-types
      onClick={() => props.clearValue()}
      size="medium"
    />
  </div>
);

const SearchInput = ({
  color = "neutral",
  error,
  errorValues,
  initialValue,
  instructions,
  name,
  onSearch,
  placeholder,
  required,
  setValue,
  ...wrapperProps
}: Props) => {
  const intl = useIntl();
  const history = useHistory();

  const [forceValue, setForceValue] = useState<"" | undefined>(undefined);
  const [inputValue, setInputValue] = useState<string | undefined | null>(initialValue);

  const debouncedSetValue = debounce(setValue, 350);

  const handleClearInput = () => {
    setValue("");
    setForceValue("");
    setInputValue("");
    setTimeout(() => {
      setForceValue(undefined);
    }, 200);
  };

  const formatOptionLabel = ({ label }: SearchOption) => (
    <div className={styles.label}>
      <div>{label}</div>
    </div>
  );

  const formatGroupLabel = ({ label }: SearchOptionGroup) => (
    <div className={styles.groupLabel}>
      <Heading3 color="gray-500" noMargin>
        {label}
      </Heading3>
    </div>
  );

  const debouncedLoadOptions =
    onSearch &&
    debounce(onSearch, 350, {
      leading: true
    });

  return (
    <SearchInputWrapper {...wrapperProps}>
      {onSearch ? (
        <Select
          aria-describedby={error ? `${name}-error` : ""}
          aria-label={placeholder ?? intl.formatMessage({ id: "dictionary.search" })}
          aria-required={required}
          classNamePrefix="react-select-async"
          /* eslint-disable @typescript-eslint/naming-convention */
          components={{
            ClearIndicator,
            DropdownIndicator: () => null,
            IndicatorSeparator: () => null,
            Input,
            Option
          }}
          defaultOptions={[]}
          formatGroupLabel={formatGroupLabel}
          formatOptionLabel={formatOptionLabel}
          id={`${name}-input`}
          isClearable
          loadOptions={debouncedLoadOptions}
          menuPortalTarget={document.body}
          noOptionsMessage={() => intl.formatMessage({ id: "dictionary.startTypingForOptions" })}
          onChange={e => {
            if (e?.onClick) {
              e.onClick();
            }
            if (e?.link) {
              history.push(e.link);
            }
          }}
          placeholder={placeholder ? placeholder : intl.formatMessage({ id: "dictionary.search" })}
          styles={searchSelectStyles(color)}
          value={null}
        />
      ) : (
        <input
          aria-invalid={error?.length ? "true" : "false"}
          aria-label={placeholder ?? name}
          className={`${styles.input} ${styles[color]}`}
          defaultValue={initialValue ?? ""}
          name={name}
          onChange={e => {
            debouncedSetValue(e.target.value ?? "");
            setInputValue(e.target.value ?? "");
          }}
          placeholder={placeholder ? placeholder : intl.formatMessage({ id: "dictionary.search" })}
          required={required}
          type="search"
          value={forceValue}
        />
      )}

      {!onSearch && inputValue && (
        <div className={styles.closeIcon}>
          <CircleButton
            ariaLabelKey="accessibility.ariaLabels.delete"
            color="gray"
            fill="clear"
            icon="xmark"
            onClick={handleClearInput}
            size="medium"
          />
        </div>
      )}

      {instructions && <TinyText>{instructions}</TinyText>}

      {error && (
        <div>
          <FormError>
            <FormattedMessage id={error} values={errorValues} />
          </FormError>
        </div>
      )}
    </SearchInputWrapper>
  );
};

export default SearchInput;
